import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { generateInvoice } from '../../utils/generateInvoice';
import ExportButton from '../../components/ExportButton';

const BRAND_BLUE = '#0B3D6E';
const BRAND_TEAL = '#0F6E56';

const STATUSES = ['pending','confirmed','processing','delivered','cancelled'];
const STATUS_LABELS = { pending:'En attente', confirmed:'Confirmée', processing:'En cours', delivered:'Livrée', cancelled:'Annulée' };
const STATUS_STYLES = {
  pending:    '#fef3c7|#d97706',
  confirmed:  '#E6F1FB|#0B3D6E',
  processing: '#f3f0ff|#7C3AED',
  delivered:  '#E1F5EE|#0F6E56',
  cancelled:  '#fee2e2|#dc2626'
};

function StatusBadge({ status }) {
  const [bg, color] = (STATUS_STYLES[status]||'#f1f5f9|#64748b').split('|');
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:600, background:bg, color, whiteSpace:'nowrap' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:color }}/>
      {STATUS_LABELS[status]||status}
    </span>
  );
}

function PDFBtn({ order, type, small }) {
  const [loading, setLoading] = useState(false);
  const label = type === 'devis' ? 'Devis' : 'Facture';
  const handle = async () => {
    setLoading(true);
    try { await generateInvoice(order, 'fr', type); toast.success(`${label} téléchargé !`); }
    catch { toast.error('Erreur PDF'); }
    finally { setLoading(false); }
  };
  if (small) return (
    <button onClick={handle} disabled={loading} style={{ fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'4px 8px', borderRadius:6, cursor:'pointer', whiteSpace:'nowrap', opacity:loading?0.6:1 }}>
      {loading?'...':`⬇ ${label}`}
    </button>
  );
  return (
    <button onClick={handle} disabled={loading} style={{
      width:'100%', background: type==='devis'?BRAND_TEAL:BRAND_BLUE, color:'#fff', border:'none',
      padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
      display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:loading?0.6:1
    }}>
      {loading?'Génération...':`⬇ Télécharger ${label} PDF`}
    </button>
  );
}

export default function Orders() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', search, statusFilter],
    queryFn: () => api.get('/orders', { params: { search:search||undefined, status:statusFilter||undefined } }).then(r=>r.data),
    refetchInterval: 15000 // rafraîchit toutes les 15s
  });

  const updateStatus = useMutation({
    mutationFn: ({id,status}) => api.put(`/orders/${id}/status`,{status}),
    onSuccess: () => { toast.success('Statut mis à jour'); qc.invalidateQueries(['orders']); qc.invalidateQueries(['stats']); },
    onError: () => toast.error('Erreur')
  });

  const deleteOrder = useMutation({
    mutationFn: id => api.delete(`/orders/${id}`),
    onSuccess: () => { toast.success('Commande supprimée'); qc.invalidateQueries(['orders']); qc.invalidateQueries(['stats']); setSelected(null); },
    onError: () => toast.error('Erreur')
  });

  const pendingCount = data?.data?.filter(o=>o.status==='pending').length || 0;

  return (
    <div style={{ padding:'1.5rem', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:'1.5rem' }}>
        <h1 style={{ fontSize:20, fontWeight:600 }}>Gestion des commandes</h1>
        {pendingCount > 0 && (
          <span style={{ background:'#fef3c7', color:'#d97706', border:'1px solid #fcd34d', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:10 }}>
            {pendingCount} en attente
          </span>
        )}
      </div>

      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
        <div style={{ display:'flex', gap:10, padding:'1rem', borderBottom:'1px solid #f1f5f9', flexWrap:'wrap', alignItems:'center' }}>
          <input type="text" placeholder="Rechercher établissement, N° commande..."
            value={search} onChange={e=>setSearch(e.target.value)}
            style={{ flex:1, minWidth:160, border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none', background:'#f8fafc' }}/>
          <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}
            style={{ border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 12px', fontSize:13, outline:'none', background:'#f8fafc', cursor:'pointer' }}>
            <option value="">Tous les statuts</option>
            {STATUSES.map(s=><option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <span style={{ fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{data?.total||0} commande(s)</span>
        </div>

        {isLoading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'#94a3b8' }}>Chargement...</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:650 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['N° commande','Établissement','Ville','Date','Statut','Actions'].map(h=>(
                    <th key={h} style={{ textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', padding:'10px 14px', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.data||[]).map(o=>(
                  <tr key={o._id} style={{ borderTop:'1px solid #f1f5f9' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:11, color:'#94a3b8', whiteSpace:'nowrap' }}>
                      {o.status==='pending' && <span style={{ display:'inline-block', width:6, height:6, background:'#d97706', borderRadius:'50%', marginRight:5, verticalAlign:'middle' }}/>}
                      {o.orderNumber}
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ fontSize:13, fontWeight:600 }}>{o.institution}</div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>{o.institutionType}</div>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:13, color:'#64748b', whiteSpace:'nowrap' }}>{o.city}</td>
                    <td style={{ padding:'10px 14px', fontSize:12, color:'#94a3b8', whiteSpace:'nowrap' }}>{new Date(o.createdAt).toLocaleDateString('fr-FR')}</td>
                    <td style={{ padding:'10px 14px' }}><StatusBadge status={o.status}/></td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                        <button onClick={()=>setSelected(o)} style={{ fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'4px 8px', borderRadius:6, cursor:'pointer', whiteSpace:'nowrap' }}>Voir</button>
                        <PDFBtn order={o} type="devis" small/>
                        <PDFBtn order={o} type="facture" small/>
                        <button onClick={()=>{ if(window.confirm('Supprimer ?')) deleteOrder.mutate(o._id); }} style={{ fontSize:11, background:'#fee2e2', border:'1px solid #fca5a5', padding:'4px 8px', borderRadius:6, cursor:'pointer', color:'#dc2626', whiteSpace:'nowrap' }}>Suppr.</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!data?.data?.length && (
              <div style={{ textAlign:'center', padding:'3rem', color:'#94a3b8' }}>Aucune commande trouvée</div>
            )}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          onClick={e=>{ if(e.target===e.currentTarget) setSelected(null); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:580, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1.2rem 1.5rem', borderBottom:'1px solid #f1f5f9', flexShrink:0 }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:600 }}>Commande {selected.orderNumber}</h2>
                <div style={{ marginTop:4 }}><StatusBadge status={selected.status}/></div>
              </div>
              <button onClick={()=>setSelected(null)} style={{ width:30, height:30, background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
            </div>

            <div style={{ overflowY:'auto', flex:1, padding:'1.5rem' }}>
              {/* Établissement */}
              <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', color:'#64748b', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #f1f5f9' }}>Établissement</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:'1.2rem' }}>
                {[['Nom',selected.institution],['Type',selected.institutionType||'—'],['Ville',selected.city],['Responsable',selected.contactName],['Téléphone',selected.phone],['Email',selected.email||'—']].map(([l,v])=>(
                  <div key={l}><div style={{ fontSize:11, color:'#94a3b8', marginBottom:2 }}>{l}</div><div style={{ fontSize:13, fontWeight:500 }}>{v}</div></div>
                ))}
              </div>

              {/* Produits */}
              <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', color:'#64748b', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #f1f5f9' }}>Produits commandés</div>
              <div style={{ marginBottom:'1.2rem' }}>
                {selected.freeText && selected.freeText.split('\n').filter(Boolean).map((l,i)=>(
                  <div key={i} style={{ background:'#f8fafc', borderRadius:8, padding:'8px 10px', fontSize:13, marginBottom:6 }}>{l}</div>
                ))}
                {(selected.items||[]).map((item,i)=>(
                  <div key={i} style={{ background:'#f8fafc', borderRadius:8, padding:'8px 10px', fontSize:13, marginBottom:6, display:'flex', justifyContent:'space-between' }}>
                    <span>{item.productName} <span style={{ color:'#94a3b8', fontSize:11 }}>({item.productRef})</span></span>
                    <span style={{ fontWeight:600 }}>×{item.quantity} — {item.unitPrice}</span>
                  </div>
                ))}
                {!selected.freeText && !selected.items?.length && <div style={{ fontSize:13, color:'#94a3b8' }}>Aucun produit enregistré</div>}
              </div>

              {/* Détails */}
              <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', color:'#64748b', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #f1f5f9' }}>Détails</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:'1rem' }}>
                {[['Livraison', selected.deliveryDate?new Date(selected.deliveryDate).toLocaleDateString('fr-FR'):'—'],['Paiement',selected.paymentMethod||'—'],['Créée le',new Date(selected.createdAt).toLocaleDateString('fr-FR')]].map(([l,v])=>(
                  <div key={l}><div style={{ fontSize:11, color:'#94a3b8', marginBottom:2 }}>{l}</div><div style={{ fontSize:13, fontWeight:500 }}>{v}</div></div>
                ))}
              </div>
              {selected.notes && (
                <div style={{ background:'#f8fafc', borderRadius:8, padding:'10px 12px', marginBottom:'1rem' }}>
                  <div style={{ fontSize:11, color:'#94a3b8', marginBottom:4 }}>Remarques</div>
                  <div style={{ fontSize:13 }}>{selected.notes}</div>
                </div>
              )}

              {/* Changer statut */}
              <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px', color:'#64748b', marginBottom:10, paddingBottom:6, borderBottom:'1px solid #f1f5f9' }}>Changer le statut</div>
              <div style={{ display:'flex', gap:10 }}>
                <select id="status-sel" defaultValue={selected.status}
                  style={{ flex:1, border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, outline:'none', background:'#f8fafc' }}>
                  {STATUSES.map(s=><option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
                <button onClick={()=>updateStatus.mutate({id:selected._id, status:document.getElementById('status-sel').value})}
                  style={{ background:BRAND_BLUE, color:'#fff', border:'none', padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                  Enregistrer
                </button>
              </div>
            </div>

            {/* Footer — PDF buttons */}
            <div style={{ padding:'1rem 1.5rem', borderTop:'1px solid #f1f5f9', display:'flex', gap:10, flexShrink:0 }}>
              <div style={{ flex:1 }}><PDFBtn order={selected} type="devis"/></div>
              <div style={{ flex:1 }}><PDFBtn order={selected} type="facture"/></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

